import { useState, useEffect } from 'react'
import { submitAwarenessRegistration } from '../services/api'

const INSTITUTION_TYPES = [
  'School (K-12)',
  'College / University',
  'Residential Society / RWA',
  'Corporate / Workplace',
  'Community Group / NGO',
  'Other',
]

const TOPIC_OPTIONS = [
  'Dog Bite Prevention & 15-Min Wash First Aid',
  'Canine Body Language & De-escalation',
  'Feeder & RWA Harmony (Safe Feeding Spots)',
  'Rabies Eradication & ABC Sterilization',
]

const AwarenessRegisterModal = ({ isOpen, onClose }) => {
  const [mounted, setMounted] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  const [formData, setFormData] = useState({
    institution_name: '',
    institution_type: 'School (K-12)',
    contact_person: '',
    phone: '',
    email: '',
    city: '',
    expected_attendees: '50 - 100',
    preferred_date: '',
    topics: ['Dog Bite Prevention & 15-Min Wash First Aid'],
    message: '',
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState('')

  // Smooth slide-in and slide-out animation lifecycle
  useEffect(() => {
    if (isOpen) {
      setMounted(true)
      const t = setTimeout(() => setIsVisible(true), 20)
      return () => clearTimeout(t)
    } else {
      setIsVisible(false)
      const t = setTimeout(() => setMounted(false), 350)
      return () => clearTimeout(t)
    }
  }, [isOpen])

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Close on ESC key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleStartClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  if (!mounted && !isOpen) return null

  const handleTopicToggle = (topic) => {
    setFormData((prev) => {
      const exists = prev.topics.includes(topic)
      const newTopics = exists
        ? prev.topics.filter((t) => t !== topic)
        : [...prev.topics, topic]
      return { ...prev, topics: newTopics }
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError('')

    try {
      const payload = {
        ...formData,
        topics: JSON.stringify(formData.topics),
      }
      const res = await submitAwarenessRegistration(payload)
      if (res && res.success !== false) {
        setSubmitSuccess(true)
      } else {
        setSubmitError(res?.message || 'Failed to submit. Please check required fields.')
      }
    } catch (err) {
      console.error('Submission error:', err)
      setSubmitError('Failed to submit. Please check connection or try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleStartClose = () => {
    setIsVisible(false)
    setTimeout(() => {
      setSubmitSuccess(false)
      setSubmitError('')
      setFormData({
        institution_name: '',
        institution_type: 'School (K-12)',
        contact_person: '',
        phone: '',
        email: '',
        city: '',
        expected_attendees: '50 - 100',
        preferred_date: '',
        topics: ['Dog Bite Prevention & 15-Min Wash First Aid'],
        message: '',
      })
      onClose()
    }, 320)
  }

  return (
    <>
      {/* Backdrop overlay with smooth fade transition */}
      <div
        className={`fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs transition-opacity duration-350 ease-in-out ${
          isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleStartClose}
      />

      {/* Slide-Up / Slide-Down Bottom-Attached Container */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 flex justify-center pointer-events-none transition-transform duration-350 ease-[cubic-bezier(0.16,1,0.3,1)] transform ${
          isVisible ? 'translate-y-0' : 'translate-y-full'
        }`}
        data-lenis-prevent
      >
        <div
          className="bg-white rounded-none w-full max-w-4xl lg:max-w-5xl 2xl:max-w-6xl shadow-2xl border-t-2 border-x border-slate-300 overflow-hidden flex flex-col max-h-[88vh] text-left pointer-events-auto mb-0"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="bg-[#FAF6F2] border-b border-orange-200/80 px-5 py-4 sm:px-8 sm:py-5 flex items-center justify-between shrink-0 rounded-none">
            <div className="space-y-0.5">
              <h3 className="text-xl sm:text-2xl font-display font-bold text-slate-900 leading-snug">
                Register Awareness Program
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 font-medium">
                Live humane education &amp; rabies safety sessions for Schools, Colleges, RWAs &amp; Organizations
              </p>
            </div>
            <button
              type="button"
              onClick={handleStartClose}
              className="w-9 h-9 bg-slate-200/80 hover:bg-slate-300 text-slate-700 hover:text-slate-950 flex items-center justify-center font-bold text-base transition-all cursor-pointer rounded-none border border-slate-300"
              title="Close"
            >
              ✕
            </button>
          </div>

          {/* Modal Body */}
          <div className="p-5 sm:p-8 overflow-y-auto no-scrollbar [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] flex-1 bg-white">
            {submitSuccess ? (
              <div className="py-8 text-center space-y-4 max-w-lg mx-auto">
                <div className="w-14 h-14 bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto text-2xl font-bold border border-emerald-300 rounded-none">
                  ✓
                </div>
                <div className="space-y-1">
                  <h4 className="text-2xl font-bold text-slate-900">Registration Received!</h4>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    Thank you, <span className="font-bold text-slate-900">{formData.contact_person}</span>! We have received your awareness workshop registration for <span className="font-bold text-slate-900">{formData.institution_name}</span>.
                  </p>
                </div>

                <div className="bg-orange-50/80 border border-orange-200 p-4 text-left rounded-none">
                  <p className="text-xs sm:text-sm text-slate-800 leading-relaxed font-medium">
                    📞 Our community outreach officer will call you at <strong className="font-mono text-brand-orange-800">{formData.phone}</strong> to coordinate schedule, equipment, and session details.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleStartClose}
                  className="w-full sm:w-auto px-8 py-3 bg-brand-orange-600 hover:bg-brand-orange-700 text-white font-bold text-xs uppercase tracking-wider transition-all shadow-sm cursor-pointer rounded-none"
                >
                  Close Window
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 text-xs sm:text-sm">
                {submitError && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs font-semibold rounded-none">
                    {submitError}
                  </div>
                )}

                {/* Row 1: Institution Name, Type, City */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  <div className="md:col-span-6 space-y-1">
                    <label className="block text-xs font-bold text-slate-800">
                      Institution / School / Society Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.institution_name}
                      onChange={(e) => setFormData({ ...formData, institution_name: e.target.value })}
                      placeholder="e.g. St. Joseph School / Palm Meadows RWA"
                      className="w-full px-3.5 py-2.5 text-xs sm:text-sm bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-brand-orange-500 transition-all font-medium rounded-none"
                    />
                  </div>

                  <div className="md:col-span-3 space-y-1">
                    <label className="block text-xs font-bold text-slate-800">
                      Institution Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.institution_type}
                      onChange={(e) => setFormData({ ...formData, institution_type: e.target.value })}
                      className="w-full px-3 py-2.5 text-xs sm:text-sm bg-slate-50 border border-slate-300 text-slate-900 focus:bg-white focus:outline-none focus:border-brand-orange-500 transition-all font-medium cursor-pointer rounded-none"
                    >
                      {INSTITUTION_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-3 space-y-1">
                    <label className="block text-xs font-bold text-slate-800">
                      City / Location
                    </label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="e.g. Bengaluru"
                      className="w-full px-3.5 py-2.5 text-xs sm:text-sm bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-brand-orange-500 transition-all font-medium rounded-none"
                    />
                  </div>
                </div>

                {/* Row 2: Contact Person, Phone, Email, Preferred Date */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <label className="block text-xs font-bold text-slate-800">
                      Contact Person Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.contact_person}
                      onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                      placeholder="e.g. Dr. Priya / Admin Lead"
                      className="w-full px-3.5 py-2.5 text-xs sm:text-sm bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-brand-orange-500 transition-all font-medium rounded-none"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="block text-xs font-bold text-slate-800">
                      Phone / WhatsApp Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="e.g. +91 98765 43210"
                      className="w-full px-3.5 py-2.5 text-xs sm:text-sm bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-brand-orange-500 transition-all font-medium font-mono rounded-none"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="block text-xs font-bold text-slate-800">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="e.g. info@school.org"
                      className="w-full px-3.5 py-2.5 text-xs sm:text-sm bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-brand-orange-500 transition-all font-medium rounded-none"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="block text-xs font-bold text-slate-800">
                      Preferred Date / Month
                    </label>
                    <input
                      type="text"
                      value={formData.preferred_date}
                      onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                      placeholder="e.g. Next Week / Upcoming Weekend"
                      className="w-full px-3.5 py-2.5 text-xs sm:text-sm bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-brand-orange-500 transition-all font-medium rounded-none"
                    />
                  </div>
                </div>

                {/* Row 3: Focus Topics Grid */}
                <div className="space-y-1.5 pt-1">
                  <label className="block text-xs font-bold text-slate-800">
                    Focus Topics of Interest
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                    {TOPIC_OPTIONS.map((topic) => {
                      const isSelected = formData.topics.includes(topic)
                      return (
                        <button
                          type="button"
                          key={topic}
                          onClick={() => handleTopicToggle(topic)}
                          className={`p-2.5 text-xs font-semibold border transition-all text-left flex items-start gap-2 cursor-pointer rounded-none ${
                            isSelected
                              ? 'bg-orange-50 border-brand-orange-500 text-brand-orange-950 font-bold shadow-xs'
                              : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                          }`}
                        >
                          <span className={`w-4 h-4 flex items-center justify-center shrink-0 text-[10px] font-bold border rounded-none ${
                            isSelected ? 'bg-brand-orange-600 border-brand-orange-600 text-white' : 'border-slate-400 text-transparent'
                          }`}>
                            ✓
                          </span>
                          <span className="leading-tight">{topic}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Row 4: Message */}
                <div className="space-y-1">
                  <label className="block text-xs font-bold text-slate-800">
                    Message / Special Requirements (Optional)
                  </label>
                  <textarea
                    rows={2}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Share details about audience size, student grades, auditorium setup, or specific concerns..."
                    className="w-full px-3.5 py-2 text-xs sm:text-sm bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-brand-orange-500 transition-all font-medium resize-none rounded-none"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-200">
                  <button
                    type="button"
                    onClick={handleStartClose}
                    className="px-5 py-2.5 text-xs font-bold text-slate-600 hover:text-slate-900 border border-slate-300 hover:bg-slate-100 transition-all cursor-pointer rounded-none"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-8 py-2.5 bg-brand-orange-600 hover:bg-brand-orange-700 text-white font-bold text-xs sm:text-sm shadow-md transition-all cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 rounded-none"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent animate-spin" />
                        <span>Submitting Request...</span>
                      </>
                    ) : (
                      <span>Submit Awareness Program Request</span>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default AwarenessRegisterModal
